"use client";
import {
  AlertTriangle,
  Copy,
  Download,
  FolderOpen,
  LoaderCircle,
  Play,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  classSkeleton,
} from "../../../lib/classfile";
import {
  byteEntropy,
  detectFileFormat,
  extractPrintableStrings,
  type DetectedFileFormat,
} from "../../../lib/file-formats";
import {
  type SourceRecoveryResult,
} from "../../../lib/source-recovery";
import {
  type ClassInfo,
  type ScanReport,
  type ScanSession,
} from "../../../lib/types";
import {
  formatBytes,
} from "../../shared/format";
import {
  downloadBlob,
} from "../../shared/io";
import {
  IconButton,
} from "../../shared/primitives";
import {
  TreeRow,
  EntryGlyph,
  BytecodeView,
  FormatOverview,
  SafeImagePreview,
  ExtractedStringsView,
  ContainerWorkbench,
  RecoveryWorkbench,
  JavaSourceEditor,
  buildTree,
  treeChildren,
  hexDump,
  annotatedJavaSource,
  type EntryMode,
  decompileClassWithRecoveredConstants,
} from "./panels";


export function ExplorerView({
  report,
  session,
  selectedPath,
  setSelectedPath,
  onCopy,
  onToast,
}: {
  report: ScanReport;
  session: ScanSession;
  selectedPath: string | null;
  setSelectedPath: (path: string) => void;
  onCopy: (value: string, message?: string) => void;
  onToast: (message: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(
    new Set(["META-INF", "net", "com", "org"]),
  );
  const [entryBytes, setEntryBytes] = useState<Uint8Array | null>(null);
  const [entryText, setEntryText] = useState("");
  const [detectedFormat, setDetectedFormat] =
    useState<DetectedFileFormat | null>(null);
  const [carvedStrings, setCarvedStrings] = useState<
    ReturnType<typeof extractPrintableStrings>
  >([]);
  const [mode, setMode] = useState<EntryMode>("preview");
  const [loadingEntry, setLoadingEntry] = useState(false);
  const [decompiled, setDecompiled] = useState<
    Record<string, SourceRecoveryResult>
  >({});
  const [vineflowerSources, setVineflowerSources] = useState<
    Record<string, SourceRecoveryResult>
  >({});
  const [decompilingPath, setDecompilingPath] = useState<string | null>(null);
  const [decompileErrors, setDecompileErrors] = useState<Record<string, string>>(
    {},
  );
  const deferredQuery = useDeferredValue(query);
  const inFlightDecompiles = useRef(new Set<string>());
  const tree = useMemo(() => buildTree(report.entries), [report.entries]);
  const selectedEntry =
    report.entries.find((entry) => entry.path === selectedPath) ?? null;
  const selectedEntryHash =
    report.entryHashes.find((entry) => entry.path === selectedPath) ?? null;
  const selectedClass =
    report.classes.find((item) => item.path === selectedPath) ?? null;
  const selectedRecovered = selectedClass
    ? report.recoveredExpressions.filter(
        (item) =>
          item.entry === selectedClass.path ||
          item.className === selectedClass.name,
      )
    : [];
  const selectedFlows = selectedClass
    ? report.dataFlows.filter(
        (item) =>
          item.entry === selectedClass.path ||
          item.steps.some((step) => step.className === selectedClass.name),
      )
    : [];
  const selectedXrefs = selectedClass
    ? report.crossReferences.filter(
        (item) =>
          item.fromClass === selectedClass.name ||
          item.toClass === selectedClass.name,
      )
    : [];
  const selectedClassInstructionCount = selectedClass
    ? selectedClass.methods.reduce(
        (total, method) => total + method.instructions.length,
        0,
      )
    : 0;
  const selectedClassIsLarge =
    Boolean(selectedClass) &&
    (selectedClassInstructionCount > 12_000 ||
      (selectedClass?.methods.length ?? 0) > 250 ||
      (selectedClass?.fields.length ?? 0) > 500);
  const matches = deferredQuery
    ? report.entries
        .filter(
          (entry) =>
            !entry.directory &&
            entry.path.toLowerCase().includes(deferredQuery.toLowerCase()),
        )
        .slice(0, 250)
    : [];

  useEffect(() => {
    if (!selectedEntry || selectedEntry.directory) return;
    let active = true;
    void Promise.resolve().then(async () => {
      if (!active) return;
      setLoadingEntry(true);
      setEntryBytes(null);
      setEntryText("");
      setDetectedFormat(null);
      setCarvedStrings([]);
      setMode("preview");
      try {
        const bytes = await session.readEntry(selectedEntry.path);
        if (!active || !bytes) return;
        setEntryBytes(bytes);
        const format = detectFileFormat(bytes, selectedEntry.path);
        setDetectedFormat(format);
        setCarvedStrings(
          extractPrintableStrings(bytes, {
            limit: 2_000,
            maxBytes: 4 * 1024 * 1024,
          }),
        );
        if (format.preview === "text" || selectedEntry.kind === "manifest") {
          const raw = new TextDecoder("utf-8", { fatal: false })
            .decode(bytes)
            .slice(0, 300_000);
          if (format.id === "json") {
            try {
              setEntryText(JSON.stringify(JSON.parse(raw), null, 2));
            } catch {
              setEntryText(raw);
            }
          } else {
            setEntryText(raw);
          }
        }
        setMode(format.container ? "unpack" : "preview");
      } finally {
        if (active) setLoadingEntry(false);
      }
    });
    return () => {
      active = false;
    };
  }, [selectedEntry, session]);

  const runDecompiler = useCallback(
    async (targetClass: ClassInfo, force = false) => {
      const path = targetClass.path;
      if (
        inFlightDecompiles.current.has(path) ||
        (!force && decompiled[path])
      ) {
        return decompiled[path] ?? null;
      }
      inFlightDecompiles.current.add(path);
      setDecompilingPath(path);
      setDecompileErrors((current) => {
        if (!current[path]) return current;
        const next = { ...current };
        delete next[path];
        return next;
      });
      try {
        const source = await decompileClassWithRecoveredConstants(
          report,
          session,
          targetClass,
        );
        setDecompiled((current) => {
          const next = { ...current, [path]: source };
          while (Object.keys(next).length > 8) {
            const oldest = Object.keys(next).find((key) => key !== path);
            if (!oldest) break;
            delete next[oldest];
          }
          return next;
        });
        return source;
      } catch (error) {
        setDecompileErrors((current) => ({
          ...current,
          [path]:
            error instanceof Error
              ? error.message
              : "The decompiler could not read this class.",
        }));
        return null;
      } finally {
        inFlightDecompiles.current.delete(path);
        setDecompilingPath((current) => (current === path ? null : current));
      }
    },
    [decompiled, report, session],
  );

  const runVineflower = useCallback(
    async (targetClass: ClassInfo, force = false) => {
      const path = targetClass.path;
      if (
        inFlightDecompiles.current.has(`vf:${path}`) ||
        (!force && vineflowerSources[path])
      ) {
        return vineflowerSources[path] ?? null;
      }
      inFlightDecompiles.current.add(`vf:${path}`);
      setDecompilingPath(`vf:${path}`);
      try {
        const source = await decompileClassWithRecoveredConstants(
          report,
          session,
          targetClass,
          "vineflower",
        );
        setVineflowerSources((current) => {
          const next = { ...current, [path]: source };
          while (Object.keys(next).length > 4) {
            const oldest = Object.keys(next).find((key) => key !== path);
            if (!oldest) break;
            delete next[oldest];
          }
          return next;
        });
        return source;
      } catch (error) {
        setDecompileErrors((current) => ({
          ...current,
          [`vf:${path}`]:
            error instanceof Error
              ? error.message
              : "Vineflower could not read this class.",
        }));
        return null;
      } finally {
        inFlightDecompiles.current.delete(`vf:${path}`);
        setDecompilingPath((current) =>
          current === `vf:${path}` ? null : current,
        );
      }
    },
    [report, session, vineflowerSources],
  );

  useEffect(() => {
    if (
      !selectedClass ||
      selectedClassIsLarge ||
      decompiled[selectedClass.path]
    ) {
      return;
    }
    void runDecompiler(selectedClass);
  }, [decompiled, runDecompiler, selectedClass, selectedClassIsLarge]);

  const downloadSelected = async () => {
    if (!selectedEntry) return;
    const bytes = entryBytes ?? (await session.readEntry(selectedEntry.path));
    if (!bytes) return;
    downloadBlob(
      new Uint8Array(bytes),
      selectedEntry.path.split("/").pop() ?? "entry.bin",
      "application/octet-stream",
    );
  };

  const downloadReconstructedClass = async () => {
    if (!selectedClass) return;
    const source =
      decompiled[selectedClass.path] ??
      (await runDecompiler(selectedClass, true));
    const reconstructedSource = source?.recovered;
    if (!reconstructedSource) {
      onToast("Decompiler output is unavailable for this class");
      return;
    }
    const reconstructed = annotatedJavaSource(
      reconstructedSource,
      selectedRecovered,
      selectedFlows,
    );
    downloadBlob(
      reconstructed,
      `${selectedClass.simpleName}.deobfuscated.java`,
      "text/x-java-source",
    );
    onToast("Reconstructed Java source downloaded");
  };

  const previewText = selectedClass
    ? decompiled[selectedClass.path]?.recovered || classSkeleton(selectedClass)
    : entryText;
  // A failed run leaves no raw text at all. Explaining that beats a silent
  // structural skeleton the analyst could mistake for decompiler output.
  const rawSourceText = selectedClass
    ? decompiled[selectedClass.path]?.raw ||
      [
        "/*",
        " * No raw decompiler output is available for this class.",
        ...(decompiled[selectedClass.path]?.errors.length
          ? decompiled[selectedClass.path].errors.map(
              (message) => ` * ${message.replaceAll("*/", "* /")}`,
            )
          : [
              " * Run the decompiler from this tab to produce it.",
            ]),
        " * The structural view below comes from JarLab's class parser.",
        " */",
        classSkeleton(selectedClass),
      ].join("\n")
    : "";
  const rawSourceEngine =
    (selectedClass && decompiled[selectedClass.path]?.engine) === "Vineflower"
      ? "Vineflower"
      : "CFR";
  const displayedSource =
    selectedClass && mode === "raw-source"
      ? rawSourceText
      : selectedClass && mode === "vineflower"
        ? vineflowerSources[selectedClass.path]?.recovered ||
          classSkeleton(selectedClass)
      : previewText;
  const selectedClassIsDecompiling =
    Boolean(selectedClass) && decompilingPath === selectedClass?.path;
  const selectedClassIsVineflowerDecompiling =
    Boolean(selectedClass) && decompilingPath === `vf:${selectedClass?.path}`;
  const selectedClassDecompileError = selectedClass
    ? decompileErrors[
        mode === "vineflower"
          ? `vf:${selectedClass.path}`
          : selectedClass.path
      ] ?? ""
    : "";
  const activeSourceResult = selectedClass
    ? mode === "vineflower"
      ? vineflowerSources[selectedClass.path]
      : decompiled[selectedClass.path]
    : undefined;
  // A run that fell back still records why. Showing it applies to CFR as much
  // as to Vineflower.
  const activeSourceWarning = activeSourceResult?.fallback
    ? (activeSourceResult.errors[0] ?? "")
    : "";

  return (
    <div className="explorer-layout">
      <aside className="tree-pane">
        <div className="tree-toolbar">
          <div className="search-field compact">
            <Search size={14} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Filter archive"
              aria-label="Filter archive entries"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} aria-label="Clear">
                <X size={12} />
              </button>
            )}
          </div>
        </div>
        <div className="tree-summary">
          <span>{report.entries.filter((entry) => !entry.directory).length} entries</span>
          <span>{formatBytes(report.totalUncompressed)}</span>
        </div>
        <div className="tree-list">
          {query
            ? matches.map((entry) => {
                return (
                  <button
                    className={`tree-search-row ${
                      selectedPath === entry.path ? "is-selected" : ""
                    }`}
                    key={entry.path}
                    type="button"
                    onClick={() => setSelectedPath(entry.path)}
                    title={entry.path}
                  >
                    <EntryGlyph kind={entry.kind} />
                    <span>{entry.path}</span>
                  </button>
                );
              })
            : treeChildren(tree).map((node) => (
                <TreeRow
                  key={node.path}
                  node={node}
                  depth={0}
                  expanded={expanded}
                  setExpanded={setExpanded}
                  selectedPath={selectedPath}
                  onSelect={(entry) => setSelectedPath(entry.path)}
                />
              ))}
        </div>
      </aside>
      <section className="entry-pane">
        {selectedEntry ? (
          <>
            <div className="entry-header">
              <div className="entry-title">
                <EntryGlyph kind={selectedEntry.kind} size={19} />
                <div>
                  <strong>{selectedEntry.path.split("/").pop()}</strong>
                  <span title={selectedEntry.path}>{selectedEntry.path}</span>
                </div>
              </div>
              <div className="entry-actions">
                <div className="mode-tabs">
                  <button
                    className={mode === "preview" ? "is-active" : ""}
                    type="button"
                    onClick={() => setMode("preview")}
                  >
                    {selectedClass ? "Recovered source" : "Preview"}
                  </button>
                  {selectedClass && (
                    <button
                      className={mode === "raw-source" ? "is-active" : ""}
                      type="button"
                      onClick={() => {
                        setMode("raw-source");
                        if (!decompiled[selectedClass.path]) {
                          void runDecompiler(selectedClass);
                        }
                      }}
                    >
                      Raw {rawSourceEngine}
                    </button>
                  )}
                  {selectedClass && (
                    <button
                      className={mode === "vineflower" ? "is-active" : ""}
                      type="button"
                      onClick={() => {
                        setMode("vineflower");
                        if (!vineflowerSources[selectedClass.path]) {
                          void runVineflower(selectedClass);
                        }
                      }}
                    >
                      Vineflower
                    </button>
                  )}
                  {selectedClass && (
                    <button
                      className={mode === "bytecode" ? "is-active" : ""}
                      type="button"
                      onClick={() => setMode("bytecode")}
                    >
                      Bytecode
                    </button>
                  )}
                  {entryBytes && (
                    <button
                      className={mode === "strings" ? "is-active" : ""}
                      type="button"
                      onClick={() => setMode("strings")}
                    >
                      Strings
                    </button>
                  )}
                  {entryBytes && detectedFormat?.container && (
                    <button
                      className={mode === "unpack" ? "is-active" : ""}
                      type="button"
                      onClick={() => setMode("unpack")}
                    >
                      Unpack
                    </button>
                  )}
                  {entryBytes && (
                    <button
                      className={mode === "deobfuscate" ? "is-active" : ""}
                      type="button"
                      onClick={() => setMode("deobfuscate")}
                    >
                      Deobfuscate
                    </button>
                  )}
                  <button
                    className={mode === "hex" ? "is-active" : ""}
                    type="button"
                    onClick={() => setMode("hex")}
                  >
                    Hex
                  </button>
                  <button
                    className={mode === "metadata" ? "is-active" : ""}
                    type="button"
                    onClick={() => setMode("metadata")}
                  >
                    Metadata
                  </button>
                </div>
                {selectedClass && (
                  <IconButton
                    label="Download reconstructed Java source"
                    onClick={() => void downloadReconstructedClass()}
                  >
                    <Sparkles size={14} />
                  </IconButton>
                )}
                <IconButton label="Download entry" onClick={downloadSelected}>
                  <Download size={14} />
                </IconButton>
              </div>
            </div>
            {loadingEntry ? (
              <div className="entry-loading">
                <LoaderCircle size={20} />
                Reading entry…
              </div>
            ) : mode === "bytecode" && selectedClass ? (
              <BytecodeView
                key={selectedClass.path}
                classInfo={selectedClass}
                onCopy={onCopy}
              />
            ) : mode === "metadata" ? (
              <div className="entry-metadata">
                <div>
                  <span>Path</span>
                  <code>{selectedEntry.path}</code>
                </div>
                <div>
                  <span>Type</span>
                  <strong>{selectedEntry.kind}</strong>
                </div>
                {detectedFormat && (
                  <>
                    <div>
                      <span>Detected format</span>
                      <strong>{detectedFormat.label}</strong>
                    </div>
                    <div>
                      <span>Format confidence</span>
                      <strong>{detectedFormat.confidence}</strong>
                    </div>
                    <div>
                      <span>MIME</span>
                      <code>{detectedFormat.mime}</code>
                    </div>
                    <div>
                      <span>Capabilities</span>
                      <strong>
                        {[
                          detectedFormat.container ? "container" : "",
                          detectedFormat.executable ? "executable" : "",
                          detectedFormat.preview,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </strong>
                    </div>
                  </>
                )}
                <div>
                  <span>Expanded size</span>
                  <strong>{formatBytes(selectedEntry.size)}</strong>
                </div>
                <div>
                  <span>Compressed size</span>
                  <strong>{formatBytes(selectedEntry.compressedSize)}</strong>
                </div>
                <div>
                  <span>Compression</span>
                  <strong>
                    {selectedEntry.size
                      ? `${Math.round(
                          (1 - selectedEntry.compressedSize / selectedEntry.size) *
                            100,
                        )}%`
                      : "0%"}
                  </strong>
                </div>
                {entryBytes && (
                  <>
                    <div>
                      <span>Entropy</span>
                      <strong>
                        {byteEntropy(
                          entryBytes.subarray(0, 8 * 1024 * 1024),
                        ).toFixed(2)}{" "}
                        bits/byte
                      </strong>
                    </div>
                    <div>
                      <span>Printable strings</span>
                      <strong>{carvedStrings.length.toLocaleString()}</strong>
                    </div>
                  </>
                )}
                {selectedEntryHash && (
                  <>
                    <div>
                      <span>SHA-256</span>
                      <code>{selectedEntryHash.sha256}</code>
                    </div>
                    <div>
                      <span>SHA-1</span>
                      <code>{selectedEntryHash.sha1}</code>
                    </div>
                    <div>
                      <span>MD5</span>
                      <code>{selectedEntryHash.md5}</code>
                    </div>
                  </>
                )}
                {selectedClass && (
                  <>
                    <div>
                      <span>Class</span>
                      <code>{selectedClass.name}</code>
                    </div>
                    <div>
                      <span>Bytecode</span>
                      <strong>
                        {selectedClass.javaVersion} · major {selectedClass.majorVersion}
                      </strong>
                    </div>
                    <div>
                      <span>Members</span>
                      <strong>
                        {selectedClass.methods.length} methods ·{" "}
                        {selectedClass.fields.length} fields
                      </strong>
                    </div>
                  </>
                )}
              </div>
            ) : mode === "strings" && entryBytes ? (
              <ExtractedStringsView strings={carvedStrings} onCopy={onCopy} />
            ) : mode === "unpack" && entryBytes ? (
              <ContainerWorkbench
                key={`${report.id}-${selectedEntry.path}-${entryBytes.length}`}
                bytes={entryBytes}
                path={selectedEntry.path}
                report={report}
                onToast={onToast}
              />
            ) : mode === "deobfuscate" && entryBytes ? (
              <RecoveryWorkbench
                key={`${report.id}-${selectedEntry.path}-${entryBytes.length}`}
                bytes={entryBytes}
                path={selectedEntry.path}
                report={report}
                onToast={onToast}
              />
            ) : (
              <div className="code-workbench">
                {(mode === "preview" ||
                  mode === "raw-source" ||
                  mode === "vineflower") &&
                  selectedClass && (
                  <div className="code-notice">
                    <span>
                      <Sparkles size={14} />
                      {mode === "vineflower" &&
                      selectedClassIsVineflowerDecompiling
                        ? "Decompiling with Vineflower…"
                        : selectedClassIsDecompiling
                        ? "Decompiling with CFR…"
                        : mode === "vineflower" &&
                            vineflowerSources[selectedClass.path]
                          ? `${vineflowerSources[selectedClass.path].profile} · quality ${vineflowerSources[selectedClass.path].quality.score}/100`
                          : decompiled[selectedClass.path]
                          ? `${decompiled[selectedClass.path].profile} · quality ${decompiled[selectedClass.path].quality.score}/100`
                          : selectedClassIsLarge
                            ? `Large class optimized · ${selectedClassInstructionCount.toLocaleString()} instructions · decompile on demand`
                          : mode === "vineflower"
                            ? "Vineflower is ready on demand"
                            : "CFR unavailable · structural fallback"}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        mode === "vineflower"
                          ? void runVineflower(selectedClass, true)
                          : void runDecompiler(selectedClass, true)
                      }
                      disabled={
                        selectedClassIsDecompiling ||
                        selectedClassIsVineflowerDecompiling
                      }
                    >
                      {selectedClassIsDecompiling ||
                      selectedClassIsVineflowerDecompiling ? (
                        <LoaderCircle size={13} />
                      ) : (
                        <Play size={13} />
                      )}
                      {mode === "vineflower"
                        ? vineflowerSources[selectedClass.path]
                          ? "Run Vineflower again"
                          : selectedClassIsVineflowerDecompiling
                            ? "Decompiling"
                            : "Run Vineflower"
                        : decompiled[selectedClass.path]
                        ? "Decompile again"
                        : selectedClassIsDecompiling
                          ? "Decompiling"
                          : "Retry CFR"}
                    </button>
                  </div>
                )}
                {selectedClassDecompileError && (
                  <div className="code-error">
                    <AlertTriangle size={14} />
                    {selectedClassDecompileError}
                  </div>
                )}
                {(mode === "preview" ||
                  mode === "raw-source" ||
                  mode === "vineflower") &&
                  activeSourceWarning && (
                    <div className="code-error">
                      <AlertTriangle size={14} />
                      {activeSourceWarning}
                    </div>
                  )}
                {(mode === "preview" ||
                  mode === "raw-source" ||
                  mode === "vineflower") &&
                selectedClass &&
                ((mode === "vineflower" &&
                  selectedClassIsVineflowerDecompiling &&
                  !vineflowerSources[selectedClass.path]) ||
                  (mode !== "vineflower" &&
                    selectedClassIsDecompiling &&
                    !decompiled[selectedClass.path])) ? (
                  <div className="entry-loading">
                    <LoaderCircle size={20} />
                    Decompiling {selectedClass.simpleName}.class with{" "}
                    {mode === "vineflower" ? "Vineflower" : "CFR"}…
                  </div>
                ) : (mode === "preview" ||
                    mode === "raw-source" ||
                    mode === "vineflower") &&
                  selectedClass ? (
                  <>
                    <JavaSourceEditor
                      source={
                        mode === "raw-source"
                          ? rawSourceText
                          : mode === "vineflower"
                            ? vineflowerSources[selectedClass.path]?.recovered ||
                              classSkeleton(selectedClass)
                          : previewText
                      }
                      classInfo={selectedClass}
                      decompiled={Boolean(decompiled[selectedClass.path])}
                      recovered={
                        mode === "raw-source" ? [] : selectedRecovered
                      }
                      flows={mode === "raw-source" ? [] : selectedFlows}
                      sourceLabel={
                        mode === "raw-source"
                          ? `Raw ${rawSourceEngine} output`
                          : mode === "vineflower"
                            ? vineflowerSources[selectedClass.path]?.fallback
                              ? "Vineflower structural fallback"
                              : "Vineflower recovered source"
                          : decompiled[selectedClass.path]?.fallback
                            ? "Recovered structural source"
                            : selectedRecovered.some(
                                  (item) =>
                                    item.patchable &&
                                    item.confidence === "high",
                                )
                              ? "Rewritten-bytecode CFR source"
                              : "Readable recovered source"
                      }
                    />
                    {mode === "preview" &&
                      decompiled[selectedClass.path]?.changes.length > 0 && (
                        <div className="source-recovery-strip">
                          <strong>Readable-source recovery</strong>
                          {decompiled[selectedClass.path].changes.map(
                            (change) => (
                              <span key={change}>{change}</span>
                            ),
                          )}
                        </div>
                      )}
                    {(selectedXrefs.length > 0 ||
                      selectedRecovered.length > 0) && (
                      <div className="ide-analysis-dock">
                        <div>
                          <span>REFERENCES</span>
                          <strong>{selectedXrefs.length}</strong>
                        </div>
                        <div className="ide-xref-list">
                          {selectedXrefs.slice(0, 14).map((reference) => {
                            const outgoing =
                              reference.fromClass === selectedClass.name;
                            const targetClass = outgoing
                              ? reference.toClass
                              : reference.fromClass;
                            const targetMethod = outgoing
                              ? reference.toMethod
                              : reference.fromMethod;
                            const target = report.classes.find(
                              (item) => item.name === targetClass,
                            );
                            return (
                              <button
                                type="button"
                                key={reference.id}
                                disabled={!target}
                                onClick={() => {
                                  if (target) setSelectedPath(target.path);
                                }}
                                title={
                                  target
                                    ? `Open ${targetClass}`
                                    : "External class"
                                }
                              >
                                {outgoing ? "calls" : "used by"}
                                <code>
                                  {targetClass}.{targetMethod}
                                </code>
                                {reference.reachable && (
                                  <span>reachable</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                ) : mode === "preview" &&
                  !selectedClass &&
                  entryBytes &&
                  detectedFormat ? (
                  <div className="artifact-preview">
                    <FormatOverview
                      format={detectedFormat}
                      bytes={entryBytes}
                      stringCount={carvedStrings.length}
                    />
                    <SafeImagePreview
                      bytes={entryBytes}
                      format={detectedFormat}
                      name={selectedEntry.path}
                    />
                    {entryText ? (
                      <pre className="plain-code-preview artifact-text">
                        <code>{entryText}</code>
                      </pre>
                    ) : (
                      <div className="binary-overview">
                        <div>
                          <span>FIRST BYTES</span>
                          <pre>
                            <code>{hexDump(entryBytes, 2048)}</code>
                          </pre>
                        </div>
                        <div>
                          <span>TOP PRINTABLE STRINGS</span>
                          {carvedStrings.slice(0, 18).map((item) => (
                            <button
                              type="button"
                              key={`${item.offset}-${item.encoding}`}
                              onClick={() =>
                                onCopy(item.value, "String copied")
                              }
                            >
                              <code>0x{item.offset.toString(16)}</code>
                              <strong>{item.value}</strong>
                            </button>
                          ))}
                          {!carvedStrings.length && (
                            <small>No printable strings recovered.</small>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <pre className="plain-code-preview">
                    <code>
                      {mode === "hex"
                        ? entryBytes
                          ? hexDump(entryBytes)
                          : "No bytes available."
                        : previewText || "No readable preview is available."}
                    </code>
                  </pre>
                )}
                <div className="code-footer">
                  <span>
                    {mode === "hex"
                      ? "HEX / ASCII"
                      : selectedClass
                        ? "JAVA"
                        : detectedFormat?.label.toUpperCase() || "BINARY"}
                  </span>
                  <span>{entryBytes ? formatBytes(entryBytes.length) : "—"}</span>
                  {displayedSource && (
                    <button
                      type="button"
                      onClick={() => onCopy(displayedSource, "View copied")}
                    >
                      <Copy size={12} />
                      Copy
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="entry-empty">
            <FolderOpen size={34} />
            <strong>Select an archive entry</strong>
            <span>Inspect text, class structure, metadata, or raw bytes.</span>
          </div>
        )}
      </section>
    </div>
  );
}
